// ABOUTME: Maps DocumentType enum values to their base document key strings.
// ABOUTME: Provides both static keys (for backward compat) and offer-scoped keys (per-programme isolation).
import { DocumentType } from '../enums.js';

/**
 * Base document key mapping without offer scoping.
 * Used for application-phase documents that are shared across programmes (e.g. personal statement).
 *
 * NOTE: Legacy data in DynamoDB may still use the old `-1` suffix (e.g. `passport-1` instead of `passport`).
 * Any code that reads documents by docKey should fall back to checking the `{baseKey}-1` variant.
 * TODO: Remove legacy `-1` fallback support once all existing applicants have been migrated.
 */
export const DocumentKey = {
  [DocumentType.AcademicTranscript]: 'academic-transcript',
  [DocumentType.Passport]: 'passport',
  [DocumentType.PersonalStatement]: 'personal-statement',
  [DocumentType.PhotoId]: 'photo-id',
  [DocumentType.CV]: 'cv',
  [DocumentType.EnglishTest]: 'english-test',
  [DocumentType.ReferenceLetter]: 'letter-of-recommendation',
  [DocumentType.AcademicCertificate]: 'academic-certificate',
  [DocumentType.ProofOfAddress]: 'proof-of-address',
  [DocumentType.ProofOfNameChange]: 'proof-of-name-change',
  [DocumentType.NameAffidavit]: 'name-affidavit',
  [DocumentType.BirthCert]: 'birth-certificate',
  [DocumentType.ProofOfImmigrationStatus]: 'proof-of-immigration-status',
  [DocumentType.ProofOfManagerialExperience]: 'proof-of-managerial-experience',
  [DocumentType.ProofOfPayment]: 'proof-of-payment',
  [DocumentType.Portfolio]: 'portfolio',
  [DocumentType.ResearchProposal]: 'research-proposal',
  [DocumentType.CustomRequest]: 'custom-request',
  [DocumentType.CustomRequirement]: 'custom-requirement',
} as const;

/**
 * Generates an offer-scoped document key for programme-level documents.
 * This ensures each programme/offer has its own isolated DynamoDB DOCUMENT# item and S3 path.
 *
 * @param docType - The document type enum value
 * @param offerId - The offer ID to scope the document to
 * @returns The offer-scoped document key string, e.g. `passport-abc123`
 */
export function getOfferScopedDocKey(docType: DocumentType, offerId: string): string {
  const baseKey = (DocumentKey as Record<string, string>)[docType];
  if (!baseKey) {
    return `${docType}-${offerId}`;
  }
  return `${baseKey}-${offerId}`;
}
